import React, { useState } from 'react'
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Space,
  Popconfirm,
  message,
  Card,
  Tag,
  Image,
  Typography,
  Row,
  Col,
  Tabs,
  Upload,
  Divider,
  InputNumber,
     // List removed - not used
} from 'antd'
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  EyeOutlined,
  UploadOutlined,
  MinusCircleOutlined,
  PlusCircleOutlined,
  PictureOutlined
} from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { productsApi, categoriesApi, uploadApi } from '../../services/api'
import type { ColumnsType } from 'antd/es/table'
import RichTextEditor from '../../components/RichTextEditor'

const { Title, Text } = Typography
const { Option } = Select
const { TabPane } = Tabs

interface Product {
  _id: string
  name: {
    en: string
    zh: string
  }
  description: {
    en: string
    zh: string
  }
  category: {
    _id: string
    name: { en: string; zh: string }
    slug: string
  }
  price: {
    currency: string
    amount: number
    displayPrice?: {
      en: string
      zh: string
    }
  }
  images: Array<{
    url: string
    isPrimary: boolean
    alt?: string
  }>
  specifications: Array<{
    key: { en: string; zh: string }
    value: { en: string; zh: string }
  }>
  features: Array<{
    en: string
    zh: string
  }>
  tags: Array<{
    en: string
    zh: string
  }>
  faqs: Array<{
    question: { en: string; zh: string }
    answer: { en: string; zh: string }
  }>
  manualUrl?: string
  videoUrl?: string
  onlineSalesUrl?: string
  isActive: boolean
  isFeatured: boolean
  isNew: boolean
  sortOrder: number
  viewCount: number
  slug: string
  createdAt: string
  updatedAt: string
}

const AdminProducts: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingProduct, setEditingProduct] = useState<Product | null>(null)
  const [uploadLoading, setUploadLoading] = useState(false)
  const [productImages, setProductImages] = useState<Product['images']>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [form] = Form.useForm()
  const queryClient = useQueryClient()

  // Fetch products
  const { data: productsData, isLoading, error } = useQuery({
    queryKey: ['admin-products', currentPage, pageSize],
    queryFn: () => productsApi.getAdminAll({ 
      page: currentPage, 
      limit: pageSize 
    }),
  })

  // Fetch categories for dropdown
  const { data: categoriesData } = useQuery({
    queryKey: ['admin-categories'],
    queryFn: () => categoriesApi.getAdminAll(),
  })

  // Create product mutation
  const createProductMutation = useMutation({
    mutationFn: productsApi.create,
    onSuccess: () => {
      message.success('Product created successfully')
      queryClient.invalidateQueries({ queryKey: ['admin-products'] })
      setIsModalOpen(false)
      form.resetFields()
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || 'Failed to create product')
    },
  })

  // Update product mutation
  const updateProductMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => 
      productsApi.update(id, data),
    onSuccess: () => {
      message.success('Product updated successfully')
      queryClient.invalidateQueries({ queryKey: ['admin-products'] })
      setIsModalOpen(false)
      form.resetFields()
      setEditingProduct(null)
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || 'Failed to update product')
    },
  })

  // Delete product mutation
  const deleteProductMutation = useMutation({
    mutationFn: productsApi.delete,
    onSuccess: () => {
      message.success('Product deleted successfully')
      queryClient.invalidateQueries({ queryKey: ['admin-products'] })
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || 'Failed to delete product')
    },
  })

  // Toggle active mutation
  const toggleActiveMutation = useMutation({
    mutationFn: productsApi.toggleActive,
    onSuccess: () => {
      message.success('Product status updated successfully')
      queryClient.invalidateQueries({ queryKey: ['admin-products'] })
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || 'Failed to update product status')
    },
  })

  // Toggle featured mutation
  const toggleFeaturedMutation = useMutation({
    mutationFn: productsApi.toggleFeatured,
    onSuccess: () => {
      message.success('Product featured status updated successfully')
      queryClient.invalidateQueries({ queryKey: ['admin-products'] })
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || 'Failed to update featured status')
    },
  })

  // Toggle new mutation
  const toggleNewMutation = useMutation({
    mutationFn: productsApi.toggleNew,
    onSuccess: () => {
      message.success('Product new status updated successfully')
      queryClient.invalidateQueries({ queryKey: ['admin-products'] })
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || 'Failed to update new status')
    },
  })

  // Image upload handler
  const handleImageUpload = async (file: File): Promise<string> => {
    setUploadLoading(true)
    try {
      const response = await uploadApi.uploadImage(file)
      message.success('Image uploaded successfully')
      return response.file.url
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to upload image')
      throw error
    } finally {
      setUploadLoading(false)
    }
  }

  // Add image to product
  const addProductImage = async (file: File) => {
    try {
      const imageUrl = await handleImageUpload(file)
      const newImage = {
        url: imageUrl,
        isPrimary: productImages.length === 0 // First image is primary
      }
      setProductImages([...productImages, newImage])
    } catch (error) {
      console.error('Failed to upload product image:', error)
    }
    return false // Prevent default upload behavior
  }

  // Remove image from product
  const removeProductImage = (index: number) => {
    const updatedImages = productImages.filter((_, i) => i !== index)
    // If we removed the primary image, make the first remaining image primary
    if (updatedImages.length > 0 && productImages[index].isPrimary) {
      updatedImages[0].isPrimary = true
    }
    setProductImages(updatedImages)
  }

  // Set primary image
  const setPrimaryImage = (index: number) => {
    const updatedImages = productImages.map((img, i) => ({
      ...img,
      isPrimary: i === index
    }))
    setProductImages(updatedImages)
  }

  // Table columns
  const columns: ColumnsType<Product> = [
    {
      title: 'Image',
      dataIndex: 'images',
      key: 'images',
      width: 80,
      render: (images: Array<{ url: string; isPrimary: boolean }>) => (
        images && images.length > 0 ? (
          <Image
            width={50}
            height={50}
            src={images[0].url}
            alt="Product image"
            style={{ objectFit: 'cover', borderRadius: '4px' }}
            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN..."
          />
        ) : (
          <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
            <span className="text-gray-400 text-xs">No Image</span>
          </div>
        )
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: { en: string; zh: string }) => (
        <div>
          <div className="font-medium">{name.en}</div>
          <div className="text-sm text-gray-500">{name.zh}</div>
        </div>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (category: any) => {
        // Handle both string ObjectId and populated category object
        if (!category) return <Tag color="gray">No Category</Tag>;
        
        if (typeof category === 'string') {
          // Category is just an ObjectId string - we can't display the name
          return <Tag color="blue">Category Set</Tag>;
        }
        
        // Category is a populated object with name
        return <Tag color="blue">{category?.name?.en || 'Unknown Category'}</Tag>;
      },
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
             render: (price: Product['price']) => (
        <div>
          <span className="font-medium">{price.currency} {price.amount}</span>
          {price.displayPrice && (
            <div className="text-sm text-gray-500">
              Display: {price.displayPrice.en}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 150,
      render: (_, record: Product) => (
        <Space direction="vertical" size="small">
          <div className="flex items-center justify-between">
            <span className="text-xs">Active</span>
            <Switch
              size="small"
              checked={record.isActive}
              onChange={() => handleToggleActive(record._id)}
            />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs">Featured</span>
            <Switch
              size="small"
              checked={record.isFeatured}
              onChange={() => handleToggleFeatured(record._id)}
            />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs">New</span>
            <Switch
              size="small"
              checked={record.isNew}
              onChange={() => handleToggleNew(record._id)}
            />
          </div>
        </Space>
      ),
    },
    {
      title: 'Sort Order',
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      width: 100,
      sorter: true,
      render: (sortOrder: number) => (
        <span>{sortOrder || 0}</span>
      ),
    },
    {
      title: 'Views',
      dataIndex: 'viewCount',
      key: 'viewCount',
      render: (viewCount: number) => (
        <span>{viewCount || 0}</span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record: Product) => (
        <Space size="middle">
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => window.open(`/products/${record._id}`, '_blank')}
            title="View Product"
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            title="Edit Product"
          />
          <Popconfirm
            title="Are you sure you want to delete this product?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              title="Delete Product"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ]

  const handleAdd = () => {
    setEditingProduct(null)
    setProductImages([])
    form.resetFields()
    setIsModalOpen(true)
  }

  const handleEdit = async (product: Product) => {
    setEditingProduct(product)
    setProductImages(product.images || [])
    
    // Handle category - it might be an object with _id or just a string ObjectId
    const categoryId = typeof product.category === 'string' 
      ? product.category 
      : product.category?._id;
    
    console.log('🔍 Form population debug:', {
      productId: product._id,
      rawCategory: product.category,
      extractedCategoryId: categoryId,
      categoryType: typeof product.category
    });
    
    // Fetch fresh product by ID to avoid any truncated fields in list payload
    try {
      const detailed = await productsApi.getById(product._id)
      const fresh = detailed?.product || product
      if (process.env.NODE_ENV === 'development') {
        console.log('[AdminProducts] handleEdit detailed.description', {
          en: fresh?.description?.en,
          zh: fresh?.description?.zh,
        })
      }
      form.setFieldsValue({
        'name.en': fresh.name.en,
        'name.zh': fresh.name.zh,
        description: { en: fresh.description.en, zh: fresh.description.zh },
        category: categoryId,
        manualUrl: fresh.manualUrl,
        videoUrl: fresh.videoUrl,
        onlineSalesUrl: fresh.onlineSalesUrl,
        isActive: fresh.isActive,
        isFeatured: fresh.isFeatured,
        isNew: fresh.isNew,
        sortOrder: fresh.sortOrder || 0,
        specifications: fresh.specifications,
        features: fresh.features,
        tags: fresh.tags || [],
      })
    } catch (e) {
      // Fallback to existing list product on error
      form.setFieldsValue({
        'name.en': product.name.en,
        'name.zh': product.name.zh,
        description: { en: product.description.en, zh: product.description.zh },
        category: categoryId,
        manualUrl: product.manualUrl,
        videoUrl: product.videoUrl,
        onlineSalesUrl: product.onlineSalesUrl,
        isActive: product.isActive,
        isFeatured: product.isFeatured,
        isNew: product.isNew,
        sortOrder: product.sortOrder || 0,
        specifications: product.specifications,
        features: product.features,
        tags: product.tags || [],
      })
    }
    setIsModalOpen(true)
  }

  const handleDelete = (productId: string) => {
    deleteProductMutation.mutate(productId)
  }

  const handleToggleActive = (productId: string) => {
    toggleActiveMutation.mutate(productId)
  }

  const handleToggleFeatured = (productId: string) => {
    toggleFeaturedMutation.mutate(productId)
  }

  const handleToggleNew = (productId: string) => {
    toggleNewMutation.mutate(productId)
  }

  const handleSubmit = (values: any) => {
    // Clean data to only include required fields (remove _id, id, etc.)
    const cleanedTags = (values.tags || []).map((tag: any) => ({
      en: tag.en || '',
      zh: tag.zh || ''
    })).filter((tag: any) => tag.en.trim() || tag.zh.trim()); // Remove empty tags

    const cleanedSpecifications = (values.specifications || []).map((spec: any) => ({
      key: {
        en: spec.key?.en || '',
        zh: spec.key?.zh || ''
      },
      value: {
        en: spec.value?.en || '',
        zh: spec.value?.zh || ''
      }
    })).filter((spec: any) => spec.key.en.trim() || spec.key.zh.trim());

    const cleanedFeatures = (values.features || []).map((feature: any) => ({
      en: feature.en || '',
      zh: feature.zh || ''
    })).filter((feature: any) => feature.en.trim() || feature.zh.trim());

    const productData = {
      name: {
        en: values['name.en'],
        zh: values['name.zh'],
      },
      description: {
        en: values?.description?.en,
        zh: values?.description?.zh,
      },
      category: values.category,
      images: productImages,
      manualUrl: values.manualUrl,
      videoUrl: values.videoUrl,
      onlineSalesUrl: values.onlineSalesUrl,
      isActive: values.isActive,
      isFeatured: values.isFeatured,
      isNew: values.isNew,
      sortOrder: values.sortOrder || 0,
      specifications: cleanedSpecifications,
      features: cleanedFeatures,
      tags: cleanedTags,
    }

    if (process.env.NODE_ENV === 'development') {
      // Debug outgoing payload for description HTML
      // Note: open devtools console to see this snapshot
      console.log('[AdminProducts] OUTGOING productData.description', {
        en: productData.description.en,
        zh: productData.description.zh,
      })
    }

    if (editingProduct) {
      updateProductMutation.mutate({ id: editingProduct._id, data: productData })
    } else {
      createProductMutation.mutate(productData)
    }
  }

  if (error) {
    return (
      <div className="p-6">
        <Card>
          <div className="text-center py-8">
            <Title level={4} type="danger">Error Loading Products</Title>
            <Text type="secondary">
              {(error as any)?.response?.data?.message || 'Failed to load products'}
            </Text>
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <Title level={2}>Products Management</Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAdd}
          size="large"
        >
          Add Product
        </Button>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={productsData?.products || []}
          rowKey="_id"
          loading={isLoading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: productsData?.pagination?.total || 0,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} products`,
            onChange: (page, size) => {
              setCurrentPage(page)
              if (size !== pageSize) {
                setPageSize(size)
                setCurrentPage(1) // Reset to first page when page size changes
              }
            },
          }}
        />
      </Card>

      {/* Add/Edit Product Modal */}
      <Modal
        title={editingProduct ? 'Edit Product' : 'Add Product'}
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false)
          form.resetFields()
          setEditingProduct(null)
          setProductImages([])
        }}
        footer={null}
        width={800}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            isActive: true,
          }}
        >
          <Tabs defaultActiveKey="basic">
            <TabPane tab="Basic Info" key="basic">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="name.en"
                    label="Product Name (English)"
                    rules={[{ required: true, message: 'Please enter product name in English' }]}
                  >
                    <Input placeholder="Enter product name in English" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="name.zh"
                    label="Product Name (Chinese)"
                    rules={[{ required: true, message: 'Please enter product name in Chinese' }]}
                  >
                    <Input placeholder="Enter product name in Chinese" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name={["description","en"]}
                    label="Description (English)"
                    rules={[{ required: true, message: 'Please enter description in English' }]}
                  >
                    <RichTextEditor
                      placeholder="Enter description in English"
                      height={150}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name={["description","zh"]}
                    label="Description (Chinese)"
                    rules={[{ required: true, message: 'Please enter description in Chinese' }]}
                  >
                    <RichTextEditor
                      placeholder="Enter description in Chinese"
                      height={150}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    name="category"
                    label="Product Category"
                    rules={[{ required: true, message: 'Please select a category' }]}
                  >
                    <Select 
                      placeholder="Select a category" 
                      size="large"
                      showSearch
                      optionFilterProp="children"
                    >
                      {categoriesData?.categories
                        ?.filter((cat: any) => cat.level === 1) // Only show subcategories
                        ?.map((cat: any) => (
                          <Option key={cat._id} value={cat._id}>
                            {cat.parent?.name?.en} &gt; {cat.name.en} ({cat.name.zh})
                          </Option>
                        ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>



              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="manualUrl" label="Manual URL">
                    <Input placeholder="https://example.com/manual.pdf" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="videoUrl" label="Video URL">
                    <Input placeholder="https://youtube.com/watch?v=..." />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item name="onlineSalesUrl" label="Where to Buy URL">
                    <Input placeholder="https://example.com/buy-product" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item name="isActive" label="Status" valuePropName="checked">
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>

              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="isFeatured" label="Featured Product" valuePropName="checked">
                    <Switch checkedChildren="Featured" unCheckedChildren="Not Featured" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="isNew" label="New Product" valuePropName="checked">
                    <Switch checkedChildren="New" unCheckedChildren="Not New" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item 
                    name="sortOrder" 
                    label="Sort Order"
                    tooltip="Lower numbers appear first in listings"
                  >
                    <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
                  </Form.Item>
                </Col>
              </Row>
            </TabPane>

            <TabPane tab={<span><PictureOutlined />Images</span>} key="images">
              <div>
                <Title level={5}>Product Images</Title>
                <div style={{ marginBottom: 16 }}>
                  <Upload
                    name="image"
                    accept="image/*"
                    showUploadList={false}
                    beforeUpload={addProductImage}
                  >
                    <Button 
                      icon={<UploadOutlined />} 
                      loading={uploadLoading}
                      size="large"
                      type="dashed"
                      style={{ width: '100%', height: 100 }}
                    >
                      <div style={{ marginTop: 8 }}>
                        {uploadLoading ? 'Uploading...' : 'Click to Upload Image'}
                      </div>
                      <div style={{ fontSize: '12px', color: '#999', marginTop: 4 }}>
                        Supports JPG, PNG, WebP
                      </div>
                    </Button>
                  </Upload>
                </div>

                {productImages.length > 0 && (
                  <div>
                    <Title level={5}>Uploaded Images ({productImages.length})</Title>
                    <Row gutter={16}>
                      {productImages.map((image, index) => (
                        <Col span={8} key={index} style={{ marginBottom: 16 }}>
                          <Card
                            size="small"
                            cover={
                              <div style={{ position: 'relative' }}>
                                <Image
                                  src={image.url}
                                  alt="Product image"
                                  style={{ width: '100%', height: 150, objectFit: 'cover' }}
                                  fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RUG8A+B4Hpg+p4Ah8CXwBfAl8AXwJfAF8C0BfAF8AXwBfAl8AXwBfAl8AXwBfAF8CmgBbAlrAU0BbAlrAU0BbAlrAU0BbAlrAU0CbAd7l/QfgG2h9EyPDMf7nOqLF9u/2/FpvqQBgEAAAAASUVORK0Jggg="
                                />
                                {image.isPrimary && (
                                  <div style={{
                                    position: 'absolute',
                                    top: 8,
                                    left: 8,
                                    background: '#52c41a',
                                    color: 'white',
                                    padding: '2px 8px',
                                    borderRadius: 4,
                                    fontSize: '12px'
                                  }}>
                                    Primary
                                  </div>
                                )}
                              </div>
                            }
                            actions={[
                              <Button
                                type="text"
                                size="small"
                                onClick={() => setPrimaryImage(index)}
                                disabled={image.isPrimary}
                              >
                                {image.isPrimary ? 'Primary' : 'Set Primary'}
                              </Button>,
                              <Button
                                type="text"
                                danger
                                size="small"
                                onClick={() => removeProductImage(index)}
                              >
                                Remove
                              </Button>
                            ]}
                          >
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  </div>
                )}

                {productImages.length === 0 && (
                  <div style={{
                    textAlign: 'center',
                    padding: 40,
                    border: '2px dashed #d9d9d9',
                    borderRadius: 6,
                    color: '#999'
                  }}>
                    <PictureOutlined style={{ fontSize: 48, marginBottom: 16 }} />
                    <div>No images uploaded yet</div>
                    <div style={{ fontSize: '12px', marginTop: 8 }}>
                      Upload images to showcase your product
                    </div>
                  </div>
                )}
              </div>
            </TabPane>

            <TabPane tab="Specifications" key="specifications">
              <Form.List name="specifications">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <Card key={key} size="small" className="mb-4">
                        <Row gutter={16}>
                          <Col span={10}>
                            <Form.Item
                              {...restField}
                              name={[name, 'key', 'en']}
                              label="Specification Name (EN)"
                            >
                              <Input placeholder="e.g., Dimensions" />
                            </Form.Item>
                          </Col>
                          <Col span={10}>
                            <Form.Item
                              {...restField}
                              name={[name, 'value', 'en']}
                              label="Value (EN)"
                            >
                              <Input placeholder="e.g., 10 x 5 x 2 cm" />
                            </Form.Item>
                          </Col>
                          <Col span={4}>
                            <Form.Item label=" ">
                              <Button
                                type="text"
                                danger
                                icon={<MinusCircleOutlined />}
                                onClick={() => remove(name)}
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                        <Row gutter={16}>
                          <Col span={10}>
                            <Form.Item
                              {...restField}
                              name={[name, 'key', 'zh']}
                              label="Specification Name (ZH)"
                            >
                              <Input placeholder="e.g., 尺寸" />
                            </Form.Item>
                          </Col>
                          <Col span={10}>
                            <Form.Item
                              {...restField}
                              name={[name, 'value', 'zh']}
                              label="Value (ZH)"
                            >
                              <Input placeholder="e.g., 10 x 5 x 2 厘米" />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Card>
                    ))}
                    <Form.Item>
                      <Button
                        type="dashed"
                        onClick={() => add()}
                        block
                        icon={<PlusCircleOutlined />}
                      >
                        Add Specification
                      </Button>
                    </Form.Item>
                  </>
                )}
              </Form.List>
            </TabPane>

            <TabPane tab="Tags" key="tags">
              <Form.List name="tags">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <Card key={key} size="small" className="mb-4">
                        <Row gutter={16}>
                          <Col span={10}>
                            <Form.Item
                              {...restField}
                              name={[name, 'en']}
                              label="Tag (English)"
                            >
                              <Input placeholder="Enter tag in English" />
                            </Form.Item>
                          </Col>
                          <Col span={10}>
                            <Form.Item
                              {...restField}
                              name={[name, 'zh']}
                              label="Tag (Chinese)"
                            >
                              <Input placeholder="Enter tag in Chinese" />
                            </Form.Item>
                          </Col>
                          <Col span={4}>
                            <Form.Item label=" ">
                              <Button
                                type="text"
                                danger
                                icon={<MinusCircleOutlined />}
                                onClick={() => remove(name)}
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Card>
                    ))}
                    <Form.Item>
                      <Button
                        type="dashed"
                        onClick={() => add()}
                        block
                        icon={<PlusCircleOutlined />}
                      >
                        Add Tag
                      </Button>
                    </Form.Item>
                  </>
                )}
              </Form.List>
            </TabPane>

            <TabPane tab="Features" key="features">
              <Form.List name="features">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <Card key={key} size="small" className="mb-4">
                        <Row gutter={16}>
                          <Col span={10}>
                            <Form.Item
                              {...restField}
                              name={[name, 'en']}
                              label="Feature (English)"
                            >
                              <Input placeholder="Enter feature in English" />
                            </Form.Item>
                          </Col>
                          <Col span={10}>
                            <Form.Item
                              {...restField}
                              name={[name, 'zh']}
                              label="Feature (Chinese)"
                            >
                              <Input placeholder="Enter feature in Chinese" />
                            </Form.Item>
                          </Col>
                          <Col span={4}>
                            <Form.Item label=" ">
                              <Button
                                type="text"
                                danger
                                icon={<MinusCircleOutlined />}
                                onClick={() => remove(name)}
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Card>
                    ))}
                    <Form.Item>
                      <Button
                        type="dashed"
                        onClick={() => add()}
                        block
                        icon={<PlusCircleOutlined />}
                      >
                        Add Feature
                      </Button>
                    </Form.Item>
                  </>
                )}
              </Form.List>
            </TabPane>


          </Tabs>

          <Divider />
          
          <div className="flex justify-end space-x-4">
            <Button
              onClick={() => {
                setIsModalOpen(false)
                form.resetFields()
                setEditingProduct(null)
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={createProductMutation.isPending || updateProductMutation.isPending}
            >
              {editingProduct ? 'Update Product' : 'Create Product'}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  )
}

export default AdminProducts
